import { useRef, useState } from "react";
import Button from "../../components/Button";
import ExitButton from "../../components/ExitButton";

const Upload = ({closeHandler}) => {
    const [lang, setLang] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [status, setStatus] = useState({type: "", message: ""});
    const fileInputRef = useRef(null);

    const formatFileSize = (size) => {
        if (!size) return "0 KB";
        if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    }

    const browseFiles = () => {
        fileInputRef.current?.click();
    }

    const setFile = (file) => {
        if (!file) return;

        setSelectedFile(file);
        setStatus({type: "", message: ""});
    }

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];

        setFile(file);
    }

    const handleDrop = (event) => {
        event.preventDefault();
        setFile(event.dataTransfer.files?.[0]);
    }

    const uploadExcel = async (event) => {
        event.preventDefault();

        if (!lang.trim() || !selectedFile || isUploading) {
            return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile);

        setIsUploading(true);
        setStatus({type: "", message: ""});

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/admin/dataset/${lang.trim()}`, {
                method: "POST",
                headers: token ? {Authorization: `Bearer ${token}`} : undefined,
                body: formData
            });

            if (!response.ok) {
                const details = await response.text();
                throw new Error(details || "The dataset could not be uploaded.");
            }

            setStatus({type: "success", message: "Dataset uploaded successfully."});
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        } catch (error) {
            setStatus({type: "error", message: error.message});
        } finally {
            setIsUploading(false);
        }
    }

    return <main className="page-shell">
        <div className="page-container max-w-5xl">
            <div className="page-header">
                <ExitButton func={closeHandler} />
                <div>
                    <p className="panel-heading">Dataset management</p>
                    <h1 className="page-heading">Upload exercises</h1>
                    <p className="page-subtitle">Import an Excel workbook and attach it to a language course.</p>
                </div>
            </div>

            <div className="grid gap-5 lg:grid-cols-[minmax(0,1.35fr)_minmax(18rem,0.65fr)]">
                <section className="panel panel-body">
                    <form className="panel-stack" onSubmit={uploadExcel}>
                        <label className="flex flex-col gap-2">
                            <span className="account-label">Language</span>
                            <input
                                className="form-field"
                                type="text"
                                value={lang}
                                placeholder="For example: Norwegian"
                                onChange={e => setLang(e.target.value)}
                            />
                        </label>

                        <div>
                            <div className="mb-2 account-label">Excel workbook</div>
                            <div
                                className="flex min-h-[16rem] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-cyan-200 bg-cyan-50/60 px-6 py-10 text-center transition hover:border-cyan-300 hover:bg-cyan-50"
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={handleDrop}
                            >
                                <div className="mb-4 grid h-16 w-20 place-items-center rounded-2xl bg-white text-3xl font-black text-cyan-800 shadow-sm">
                                    XLSX
                                </div>
                                <h2 className="text-xl font-bold text-cyan-950">Drop your spreadsheet here</h2>
                                <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
                                    Use an `.xlsx` file with the expected exercise sheets and vocabulary columns.
                                </p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    className="hidden"
                                    onChange={handleFileChange}
                                />
                                <Button
                                    text="Choose file"
                                    func={browseFiles}
                                    variant="secondary"
                                    className="mt-5"
                                />
                            </div>
                        </div>

                        {selectedFile && <div className="list-row">
                            <div className="min-w-0">
                                <div className="truncate font-bold text-cyan-950">{selectedFile.name}</div>
                                <div className="mt-1 text-sm text-slate-500">{formatFileSize(selectedFile.size)}</div>
                            </div>
                            <Button text="Replace" func={browseFiles} variant="ghost" />
                        </div>}

                        {status.message && <div
                            className={`rounded-xl border p-4 text-sm font-semibold ${
                                status.type === "success"
                                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                : "border-red-200 bg-red-50 text-red-700"
                            }`}
                            role="status"
                        >
                            {status.message}
                        </div>}

                        <div className="flex flex-wrap justify-end gap-3">
                            <Button text="Cancel" func={closeHandler} variant="secondary" />
                            <Button
                                text={isUploading ? "Uploading..." : "Upload dataset"}
                                type="submit"
                                clickable={Boolean(lang.trim() && selectedFile && !isUploading)}
                            />
                        </div>
                    </form>
                </section>

                <aside className="panel panel-body h-fit">
                    <p className="panel-heading">Before uploading</p>
                    <div className="mt-4 panel-stack text-sm leading-6 text-slate-600">
                        <div className="notice-panel">
                            Confirm the language name matches the course language you want the exercises to appear under.
                        </div>
                        <div className="list-row !items-start">
                            <span className="status-pill">1</span>
                            <span>Keep one exercise category per sheet.</span>
                        </div>
                        <div className="list-row !items-start">
                            <span className="status-pill">2</span>
                            <span>Include the required word, translation, filename, bin, weight, and name columns.</span>
                        </div>
                        <div className="list-row !items-start">
                            <span className="status-pill">3</span>
                            <span>Large files may take a few moments to process after upload.</span>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    </main>
}

export default Upload;
